import { defineStore, storeToRefs } from 'pinia';
import { computed, onBeforeMount, ref } from 'vue';

import type { Canonicals, GlobalConfig, RoutingOptions } from '@/types/configuration';
import type { Locale } from '@/types/locale';

import { i18n } from '@/shared-instance';
import { useFiltersStore } from '@/stores/useFiltersStore';
import { Language } from '@/types/locale';

const APIKEY_ALLOWED_LOCALES: Partial<Record<string, Locale[]>> = {
  YoubookMARSEILLEThyOfsHmNzQLZr36Fq4p24Pt6QrMpwKUJym32glspcUrLBER: ['fr-FR', 'en-US', 'en-GB'],
};

export const useGlobalConfigurationStore = defineStore('globalConfiguration', () => {
  /* VARIABLES */
  const globalConfiguration = ref<GlobalConfig | null>(null);
  const routingOptions = ref<RoutingOptions[] | null>(null);
  const canonicals = ref<Canonicals>({});
  const currentLocale = ref<Locale>('fr-FR'); // !! i18n use 'fr'|'en'|'es'|'de' and filters use 'fr-FR'|'en-US'|'es-ES'|'en-US' !!
  const apiURL = ref('');
  const assetsURL = ref('');

  /* METHODS */
  function setAPIEnv() {
    // const isPreprod = window.location.origin.includes('localhost') || window.location.origin.includes('.rc-preprod.com') || window.location.origin.includes('preprod.marseille-tourisme.com') || window.location.origin.includes('marseille-tourisme.wp.rc-preprod.com');
    const isPreprod = window.location.origin.includes('.rc-preprod.com') || window.location.origin.includes('preprod.marseille-tourisme.com') || window.location.origin.includes('marseille-tourisme.wp.rc-preprod.com');
    isPreprod ? apiURL.value = 'https://public.api.youbook.rc-preprod.com/v3' : apiURL.value = 'https://public.api.youbook.travel/v3';
    // apiURL.value = 'http://api.youbook.rc-dev.com:8083/v3';
    isPreprod ? assetsURL.value = 'https://components.youbook.rc-preprod.com/activities_svg' : assetsURL.value = 'https://components.youbook.travel/activities_svg';
  }

  function setCurrentLocale(locale: Locale) {
    const allowedLocales = globalConfiguration.value?.apikey
      ? APIKEY_ALLOWED_LOCALES[globalConfiguration.value.apikey]
      : undefined;
    if (allowedLocales && !allowedLocales.includes(locale)) {
      locale = 'en-US';
    }
    switch (locale) {
      case 'fr':
      case 'fr-FR':
        i18n.global.locale.value = 'fr';
        currentLocale.value = Language.FRENCH;
        break;
      case 'es':
      case 'es-ES':
        i18n.global.locale.value = 'es';
        currentLocale.value = Language.SPANISH;
        break;
      case 'de':
      case 'de-DE':
        i18n.global.locale.value = 'de';
        currentLocale.value = Language.GERMAN;
        break;
      case 'it':
      case 'it-IT':
        i18n.global.locale.value = 'it';
        currentLocale.value = Language.ITALIAN;
        break;
      default:
        i18n.global.locale.value = 'en';
        currentLocale.value = Language.ENGLISH;
    }

    const filtersStore = useFiltersStore();
    const { filters } = storeToRefs(filtersStore);
    const customFilterImported = filters.value.filters.find(it => it.filterProperties.type === 'custom_filter' && it.filterProperties.target === 'product' && it.filterProperties.field === 'imported_in_langs');
    if (!customFilterImported) {
      filters.value.filters.push({
        filterProperties: {
          type: 'custom_filter',
          target: 'product',
          field: 'imported_in_langs',
        },
        selection: currentLocale.value,
      });
    }
    else {
      customFilterImported.selection = currentLocale.value;
    }
    filtersStore.saveToStorage(filters.value);
  }

  async function setGlobalConfiguration(config: GlobalConfig) {
    globalConfiguration.value = config;

    const defaultOptions = {
      hasModeMap: false,
      hasCalendar: true,
      hasDetailView: true,
      hasFavorites: true,
      hasThemes: true,
      hasFilters: { price: true },
    };

    const currentOptions = globalConfiguration.value.options || {};

    globalConfiguration.value.options = {
      ...defaultOptions,
      ...currentOptions,
      hasFilters: {
        ...defaultOptions.hasFilters,
        ...(currentOptions.hasFilters || {}),
      },
    };

    const { initializeHomeURL } = useFiltersStore();
    const filterService = useFiltersStore();
    filterService.loadFromStorage();
    if (globalConfiguration.value?.homeUrl && window.location.href === globalConfiguration.value.homeUrl) {
      initializeHomeURL();
    }
    if (globalConfiguration.value?.diffusion?.establishmentId) {
      filterService.setLocationFilters(globalConfiguration.value.diffusion);
    }
    await setRoutingOptions();
    await setCanonicals();
  }

  async function setRoutingOptions() {
    if (globalConfiguration.value?.routingEndpoint) {
      try {
        const routingEndpointFetch = await fetch(`${globalConfiguration.value.routingEndpoint}`, { method: 'GET' });
        routingOptions.value = await routingEndpointFetch.json();
      }
      catch (err) {
        console.error('No routing data retrieved', err);
      }
    }
  }

  function resolveCanonicalEndpointLanguage(canonicalsEndpoint: string): string {
    const allowedLocales = globalConfiguration.value?.apikey
      ? APIKEY_ALLOWED_LOCALES[globalConfiguration.value.apikey]
      : undefined;

    if (!allowedLocales)
      return canonicalsEndpoint;

    const allowedLanguageCodes = [...new Set(allowedLocales.map(locale => locale.substring(0, 2)))];

    try {
      const endpointUrl = new URL(canonicalsEndpoint);
      const currentLang = endpointUrl.searchParams.get('lang');
      if (currentLang && !allowedLanguageCodes.includes(currentLang)) {
        endpointUrl.searchParams.set('lang', 'en');
        return endpointUrl.toString();
      }
    }
    catch {
      // malformed URL — return unchanged
    }

    return canonicalsEndpoint;
  }

  async function setCanonicals() {
    if (globalConfiguration.value?.canonicalsEndpoint) {
      const canonicalsEndpoint = resolveCanonicalEndpointLanguage(globalConfiguration.value.canonicalsEndpoint);
      try {
        const canonicalEndpointFetch = await fetch(canonicalsEndpoint, { method: 'GET' });
        canonicals.value = await canonicalEndpointFetch.json();
      }
      catch (err) {
        console.error('No canonicals data retrieved', err);
      }
    }
  }

  onBeforeMount(() => {
    setAPIEnv();
  });

  const componentsBaseUrl = computed(() => assetsURL.value.replace('/activities_svg', ''));

  return {
    globalConfiguration,
    routingOptions,
    canonicals,
    currentLocale,
    apiURL,
    assetsURL,
    componentsBaseUrl,
    setCurrentLocale,
    setGlobalConfiguration,
  };
});
